"use client";

import React, { useState } from "react";
import Swal from "sweetalert2";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import ThemeToggle from "../components/ui/themeToggle";
import { getRegistrationSourceUrl } from "../config/platforms";
import { cn } from "../lib/utils";
import { sanitizeUsername } from "../lib/username";
import {
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandInstagram,
  IconBrandFacebook,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";

export default function Registre() {
  const initialSourceUrl = getRegistrationSourceUrl();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    sourceUrl: initialSourceUrl,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const senhaRegex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[\$%\*#@!&]).{8,}$/;

    if (
      !formData.firstname ||
      !formData.lastname ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      return Swal.fire({
        icon: "error",
        title: "Campos obrigatórios",
        text: "Por favor, preencha todos os campos!",
      });
    }

    if (!senhaRegex.test(formData.password)) {
      return Swal.fire({
        icon: "error",
        title: "Senha inválida",
        html:
          "A senha deve conter:<br/>" +
          "- Pelo menos <b>1 letra maiúscula</b><br/>" +
          "- Pelo menos <b>1 número</b><br/>" +
          "- Pelo menos <b>1 caractere especial</b> ($ % * # @ ! &)<br/>" +
          "- Mínimo de 8 caracteres",
      });
    }

    if (formData.password !== formData.confirmPassword) {
      return Swal.fire({
        icon: "error",
        title: "Senhas não conferem",
        text: "As senhas digitadas são diferentes.",
      });
    }

    const normalized = sanitizeUsername(`${formData.firstname} ${formData.lastname}`);
    if (!normalized.username) {
      return Swal.fire({ icon: "error", title: "Usuário inválido", text: "Informe um nome com pelo menos um caractere permitido." });
    }
    if (normalized.removed.length > 0) {
      console.log("[Cadastro] Caracteres proibidos removidos", {
        campo: "username",
        caracteresRemovidos: normalized.removed,
        senhaAlterada: false,
        emailAlterado: false,
      });
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: normalized.username,
            firstName: formData.firstname,
            lastName: formData.lastname,
            email: formData.email,
            password_hash: formData.password,
            confirm_password: formData.confirmPassword,
            source_url: formData.sourceUrl,
          }),
        }
      );

      const data = await response.json().catch(() => null);

      if (response.ok) {
        Swal.fire({
          title: "Sucesso!",
          text: "Cadastro realizado com sucesso! Aguarde a confirmação do seu cadastro.",
          icon: "success",
        });
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          password: "",
          confirmPassword: "",
          sourceUrl: initialSourceUrl,
        });
      } else {
        const rawMessage =
          data && typeof data.message === "string"
            ? data.message
            : "";

        Swal.fire({
          icon: "error",
          title: "Erro ao registrar",
          text: data?.keycloak_sync_status && rawMessage
            ? rawMessage
            : getFriendlyRegisterMessage(response.status, rawMessage),
        });
      }
    } catch (error) {
      console.error("Erro ao registrar:", error);
      Swal.fire({
        icon: "error",
        title: "Erro de conexão",
        text: "Não foi possível conectar ao servidor.",
      });
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <ThemeToggle />
        <div className="flex justify-center">
          <img
            src="/img/Vinkings1_logo_92x92.png"
            alt="Logo Vikings"
            className="auth-logo"
          />
        </div>
        <h1 className="auth-title">
          Crie sua conta
        </h1>

        <p className="auth-description">Preencha seus dados para solicitar o cadastro.</p>

        <form className="mt-6" onSubmit={handleSubmit}>
          <div className="mb-4 flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2">
            <LabelInputContainer>
              <Label htmlFor="firstname">Primeiro nome *</Label>
              <Input
                id="firstname"
                placeholder="Maria"
                autoComplete="given-name"
                type="text"
                value={formData.firstname}
                onChange={handleChange}
                maxLength={100}
              />
            </LabelInputContainer>
            <LabelInputContainer>
              <Label htmlFor="lastname">Sobrenome *</Label>
              <Input
                id="lastname"
                placeholder="Silva"
                autoComplete="family-name"
                type="text"
                value={formData.lastname}
                onChange={handleChange}
                maxLength={100}
              />
            </LabelInputContainer>
          </div>

          <LabelInputContainer className="mb-4">
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              placeholder="voce@exemplo.com"
              autoComplete="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              maxLength={100}
            />
          </LabelInputContainer>

          <LabelInputContainer className="mb-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="password">Senha *</Label>
              <span className="password-help-control">
                <button
                  type="button"
                  className="password-help-button"
                  aria-label="Requisitos para criar uma senha"
                  aria-describedby="password-requirements"
                  onClick={() => Swal.fire({
                    icon: "info",
                    title: "Como criar sua senha",
                    text: "Use 8 ou mais caracteres, pelo menos uma letra maiúscula, um número e um dos símbolos: $ % * # @ ! &.",
                    confirmButtonText: "Entendi",
                  })}
                >
                  <span aria-hidden="true">?</span>
                </button>
                <span id="password-requirements" role="tooltip" className="password-help-tooltip">
                  Use 8 ou mais caracteres, uma maiúscula, um número e um símbolo ($ % * # @ ! &).
                </span>
              </span>
            </div>
            <PasswordField
              id="password"
              placeholder="Crie sua Senha"
              value={formData.password}
              onChange={handleChange}
              visible={showPassword}
              onToggleVisibility={() => setShowPassword((value) => !value)}
            />
          </LabelInputContainer>

          <LabelInputContainer className="mb-6">
            <Label htmlFor="confirmPassword">Repetir Senha *</Label>
            <PasswordField
              id="confirmPassword"
              placeholder="Repita sua Senha"
              value={formData.confirmPassword}
              onChange={handleChange}
              visible={showConfirmPassword}
              onToggleVisibility={() =>
                setShowConfirmPassword((value) => !value)
              }
            />
          </LabelInputContainer>

          <button
            className="group/btn relative block h-10 w-full rounded-md bg-gradient-to-br from-black to-neutral-600 font-medium text-white shadow-[0px_1px_0px_0px_#ffffff40_inset,0px_-1px_0px_0px_#ffffff40_inset] dark:bg-zinc-800 dark:from-zinc-900 dark:to-zinc-900 dark:shadow-[0px_1px_0px_0px_#27272a_inset,0px_-1px_0px_0px_#27272a_inset]"
            type="submit"
          >
            Criar conta &rarr;
            <BottomGradient />
          </button>

          <div className="mt-4 text-center">
            <a
              href="/recuperar-senha"
              className="text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              Esqueceu a senha? Recuperar aqui.
            </a>
          </div>

          <div className="my-8 h-[1px] w-full bg-gradient-to-r from-transparent via-neutral-300 to-transparent dark:via-neutral-700" />

          <div className="flex justify-center gap-8 mt-6">
            <OAuthButton icon={<IconBrandGithub size={24} />} provider="GitHub" />
            <OAuthButton icon={<IconBrandGoogle size={24} />} provider="Google" />
            <OAuthButton icon={<IconBrandInstagram size={24} />} provider="Instagram" />
            <OAuthButton icon={<IconBrandFacebook size={24} />} provider="Facebook" />
          </div>
        </form>
      </div>
    </div>
  );
}

function getFriendlyRegisterMessage(status: number, message: string) {
  const normalizedMessage = message.toLowerCase();

  if (status === 400 && normalizedMessage.includes("origem invalida")) {
    return "O cadastro so e permitido quando o acesso vier de uma URL de origem cadastrada.";
  }

  if (status === 400 && normalizedMessage.includes("plataforma invalida")) {
    return "A plataforma identificada para este dominio nao e permitida. Verifique a configuracao do site.";
  }

  if (normalizedMessage.includes("todos os campos sao obrigatorios")) {
    return "Preencha todos os campos obrigatorios antes de continuar.";
  }

  if (normalizedMessage.includes("e-mail")) {
    return message;
  }

  if (status === 503) {
    return "O serviço de cadastro está indisponível no momento. Tente novamente em instantes.";
  }

  return "Verifique os dados ou tente novamente.";
}

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("flex w-full flex-col space-y-2", className)}>
      {children}
    </div>
  );
};

const PasswordField = ({
  id,
  placeholder,
  value,
  onChange,
  visible,
  onToggleVisibility,
}: {
  id: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  visible: boolean;
  onToggleVisibility: () => void;
}) => {
  return (
    <div className="relative">
      <Input
        id={id}
        placeholder={placeholder}
        autoComplete="new-password"
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        maxLength={100}
        className="pr-11"
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
        className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-neutral-500 transition hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-white"
      >
        {visible ? <IconEyeOff size={18} /> : <IconEye size={18} />}
      </button>
    </div>
  );
};

const OAuthButton = ({
  icon,
  provider,
}: {
  icon: React.ReactNode;
  provider: string;
}) => (
  <div className="relative group">
    <button
      className="p-2 rounded-md bg-gray-100 dark:bg-zinc-900 shadow hover:bg-gray-200 dark:hover:bg-zinc-800 transition"
      type="button"
      aria-label={provider}
      title={provider}
    >
      {icon}
    </button>
    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition bg-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
      {provider}
    </div>
  </div>
);

const BottomGradient = () => {
  return (
    <>
      <span className="absolute inset-x-0 -bottom-px block h-px w-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 transition duration-500 group-hover/btn:opacity-100" />
      <span className="absolute inset-x-10 -bottom-px mx-auto block h-1/2 w-1/2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-0 blur-sm transition duration-500 group-hover/btn:opacity-100" />
    </>
  );
};
